/* eslint-disable max-len */
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store/app.reducer';
import { Observable, Subscription } from 'rxjs';
import { FormControl } from '@angular/forms';
import { PaginationParams } from 'src/app/model/api/pagination-params';
import { debounceTime, filter, map } from 'rxjs/operators';
import * as DeviceActions from 'src/app/store/device/device.actions';
import { TranslateService } from '@ngx-translate/core';
import { Site } from 'src/app/model/site';
import { DatePipe } from '@angular/common';
import { selectCurrentSite } from 'src/app/store/site/site.selector';
import { selectCurrentDeviceStatus, selectMtmaActiveTask, selectMtmaSpecialPlanningModify, selectMtmaSpecialPlannings, selectMtmaSpecialPlanningsMaxData, selectMtmaSpecialPlanningsPagination } from 'src/app/store/device/device.selector';
import { ItemsPagination, MtmaMaxData } from 'src/app/model/api/data-rest';
import { TableLazyLoadEvent } from 'primeng/table';
import { pageSortChange } from 'src/app/services/table';
import { ConfirmationService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { StandardDesignParameters, StandardMtmaLayout, StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { selectDeviceTabIndex } from 'src/app/store/home/home.selector';
import { DeviceStatus } from 'src/app/model/device-status';
import { MtmaControlAccessSpecialPlanningDialogComponent } from '../mtma-control-access-special-planning-dialog/mtma-control-access-special-planning-dialog.component';
import { MtmaTimeslice } from 'src/app/model/mtma-rules-access';
import { MtmaTask } from 'src/app/model/mtma-task';
import moment from 'moment';
import { MTMA_SPECIAL_PLANNING_DEFAULT_SORT_COLUMN, MTMA_SPECIAL_PLANNING_DEFAULT_SORT_COLUMN_DIRECTION, MTMA_SPECIAL_PLANNING_PAGE_DEFAULT_SIZE, MTMA_SPECIAL_PLANNING_TABLE_HEADER } from 'src/app/conf/control-access-mtma';
import { FlagTimeslice } from 'src/app/model/mtma-control-access';

@Component({
	selector: 'app-mtma-control-access-special-planning-list',
	templateUrl: './mtma-control-access-special-planning-list.component.html',
	providers: [ConfirmationService, DialogService]
})

export class MtmaControlAccessSpecialPlanningListComponent implements OnInit, OnDestroy {
	site$: Observable<Site | undefined>;
	siteSub: Subscription | undefined;
	specialPlannings$: Observable<MtmaTimeslice[] | undefined>;
	specialPlanningsSub: Subscription | undefined;
	specialPlanningsPagination$: Observable<ItemsPagination | undefined>;
	specialPlanningsPaginationSub: Subscription | undefined;
	specialPlanningsMaxData$: Observable<MtmaMaxData | undefined>;
	specialPlanningsMaxDataSub: Subscription | undefined;
	specialPlanningModify$: Observable<boolean | undefined>;
	specialPlanningModifySub: Subscription | undefined;
	tabIndex: number;
	tabIndex$: Observable<number | undefined>;
	tabIndexSub: Subscription | undefined;
	deviceStatus: DeviceStatus;
	deviceStatus$: Observable<DeviceStatus | undefined>;
	deviceStatusSub: Subscription | undefined;
	mtmaActiveTask$: Observable<MtmaTask | undefined>;
	mtmaActiveTaskSub: Subscription | undefined;

	searchFilter: FormControl = new FormControl('');
	filterSub: Subscription;
	filter: '';

	specialPlanningsColumns = MTMA_SPECIAL_PLANNING_TABLE_HEADER;
	specialPlanningsPagingSub: Subscription;
	specialPlanningsPageSortSub: Subscription;
	specialPlanningsPaging: PaginationParams = {
		size: MTMA_SPECIAL_PLANNING_PAGE_DEFAULT_SIZE,
		page: 1,
		sort: MTMA_SPECIAL_PLANNING_DEFAULT_SORT_COLUMN,
		order: MTMA_SPECIAL_PLANNING_DEFAULT_SORT_COLUMN_DIRECTION,
	};
	specialPlanningsPageSize: number = MTMA_SPECIAL_PLANNING_PAGE_DEFAULT_SIZE;
	// specialPlanningsPageSizeOptions = SPECIAL_PLANNING_PAGE_SIZE_OPTIONS;
	totalRecords: number;

	specialPlanningsMaxSizeMemorySub: Subscription;
	specialPlanningsMemorySize = 1;
	specialPlanningsMaxMemorySize = 1;

	specialPlanningsLoading: boolean;
	specialPlannings: MtmaTimeslice[];

	currentSite: Site;
	isActiveTask = false;

	specialPlanningConfirmDeleteSub: Subscription;
	online: boolean;

	dateTransform: DatePipe;
	ref: DynamicDialogRef | undefined;

	acceptButtonStyle: string;
	rejectButtonStyle: string;

	thereIsOldTimeslice: boolean;

	opened = false;

	screenWidth: number;
	MIN_MD_WIDTH = StandardDesignParameters.MAX_MD_WIDTH;


	daysOfWeek = new Map<number, string>([
		[0, 'SUN'],
		[1, 'MON'],
		[2, 'TUE'],
		[3, 'WED'],
		[4, 'THU'],
		[5, 'FRI'],
		[6, 'SAT'],
	]);

	constructor(
		private store: Store<AppState>,
		public confirmationService: ConfirmationService,
		private translate: TranslateService,
		public dialogService: DialogService,
	) {
		this.initSelectors();
	}

	initSelectors(): void {
		this.site$ = this.store.select(selectCurrentSite);
		this.specialPlannings$ = this.store.select(selectMtmaSpecialPlannings);
		this.specialPlanningsPagination$ = this.store.select(selectMtmaSpecialPlanningsPagination);
		this.specialPlanningsMaxData$ = this.store.select(selectMtmaSpecialPlanningsMaxData);
		this.specialPlanningModify$ = this.store.select(selectMtmaSpecialPlanningModify);
		this.tabIndex$ = this.store.select(selectDeviceTabIndex);
		this.deviceStatus$ = this.store.select(selectCurrentDeviceStatus);
		this.mtmaActiveTask$ = this.store.select(selectMtmaActiveTask);
	}

	ngOnInit(): void {
		this.thereIsOldTimeslice = false;
		const currentLang = this.translate.currentLang;

		this.dateTransform = new DatePipe(currentLang);

		this.siteSub = this.site$.subscribe(currentSite => {
			this.currentSite = currentSite;
		});
		this.tabIndexSub = this.tabIndex$?.subscribe(
			async (tabIndex: number) => {
				this.tabIndex = tabIndex;
				this.dispatchGetSpecialPlanningsPaging();
			});

		this.subscribeAll();
		this.onResize();
	}

	subscribeAll(): void {
		this.filterSub = this.searchFilter.valueChanges.pipe(
			debounceTime(500),
			map(v => v.trim().toLowerCase())
		).subscribe(value => {
			this.specialPlanningsLoading = true;
			this.filter = value;
			this.dispatchGetSpecialPlanningsPaging();
		});

		this.dispatchGetSpecialPlanningsPaging();
		this.specialPlanningsSub = this.specialPlannings$.subscribe(specialPlannings => {
			this.thereIsOldTimeslice = false;
			this.specialPlannings = specialPlannings.filter(sp => sp.Type === 2);
			this.correctSpecialPlannings();
			this.specialPlanningsLoading = false;
		});

		this.specialPlanningsPagingSub = this.specialPlanningsPagination$
			.pipe(filter(p => !!p))
			.subscribe(paging => {
				this.totalRecords = paging.TotalCount;
			});

		this.specialPlanningsMaxSizeMemorySub = this.specialPlanningsMaxData$
			.pipe(filter(p => !!p))
			.subscribe(specialPlanningsMaxData => {
				this.specialPlanningsMemorySize = specialPlanningsMaxData.Current;
				this.specialPlanningsMaxMemorySize = specialPlanningsMaxData.Max;
			});

		this.specialPlanningModifySub = this.specialPlanningModify$
			.pipe(filter(mtmaTimesliceModify => !!mtmaTimesliceModify))
			.subscribe(() => {
				this.dispatchGetSpecialPlanningsPaging();
			});
		this.deviceStatusSub = this.deviceStatus$.subscribe(deviceStatus => {
			this.deviceStatus = deviceStatus;
			if (this.deviceStatus !== undefined) {
				this.online = deviceStatus.Online;
			}
		});
		this.mtmaActiveTaskSub = this.mtmaActiveTask$.subscribe(mtmaActiveTask => {
			if (mtmaActiveTask.Id !== '') {
				this.isActiveTask = true;
			} else {
				this.isActiveTask = false;
			}
		});
	}

	ngOnDestroy(): void {
		this.specialPlanningModifySub?.unsubscribe();
		this.siteSub?.unsubscribe();
		this.specialPlanningsMaxSizeMemorySub?.unsubscribe();
		this.tabIndexSub?.unsubscribe();
		this.deviceStatusSub?.unsubscribe();
		this.mtmaActiveTaskSub?.unsubscribe();
		this.specialPlanningsPagingSub?.unsubscribe();
	}


	loadSpecialPlannings(event: TableLazyLoadEvent) {
		this.specialPlanningsLoading = true;

		this.specialPlanningsPaging = pageSortChange(this.specialPlanningsPaging, event);

		this.dispatchGetSpecialPlanningsPaging();
	}

	dispatchGetSpecialPlanningsPaging(): void {
		if (this.isSelectTab()) {
			this.store.dispatch(DeviceActions.getMtmaSpecialPlannings({
				likeName: this.filter,
				sort: this.specialPlanningsPaging.sort, order: this.specialPlanningsPaging.order, size: this.specialPlanningsPaging.size, page: this.specialPlanningsPaging.page
			}));
		}
	}

	isControlAccess(): boolean {
		// TAB INDEX FOR MTMACONNECT IS 8 tab only for mtma
		// trigger GET apis is based on open accordion, not based on tabIndex number
		return true;
	}

	isSelectTab(): boolean {
		return this.isControlAccess() && this.opened;
	}

	addSpecialPlanning(): void {
		this.store.dispatch(DeviceActions.resetMtmaSpecialPlanningForm());
		this.ref = this.dialogService.open(MtmaControlAccessSpecialPlanningDialogComponent, {
			header: this.translate.instant('ADD_SPECIAL_PLANNING'),
			width: '80vw',
			breakpoints: { '1600px': '98vw' },
			contentStyle: { overflow: 'auto' },
		});
	}

	editSpecialPlanning(specialPlanning: MtmaTimeslice): void {
		this.store.dispatch(DeviceActions.resetMtmaSpecialPlanningForm());
		this.ref = this.dialogService.open(MtmaControlAccessSpecialPlanningDialogComponent, {
			header: this.translate.instant('EDIT_SPECIAL_PLANNING'),
			width: '80vw',
			breakpoints: { '1600px': '98vw' },
			contentStyle: { overflow: 'auto' },
			data: {
				specialPlanning
			}
		});
	}

	deleteSpecialPlannig(specialPlannings: MtmaTimeslice): void {
		this.acceptButtonStyle = "p-button-danger";
		this.rejectButtonStyle = "p-button-secondary";
		this.confirmationService.confirm({
			header: this.translate.instant('DELETE_SPECIAL_PLANNING'),
			message: this.translate.instant('DELETE_SPECIAL_PLANNING_WARNING'),
			acceptLabel: this.translate.instant('CONFIRM'),
			rejectLabel: this.translate.instant('CANCEL'),
			icon: 'pi cm-alert',
			accept: () => {
				this.store.dispatch(DeviceActions.deleteMtmaSpecialPlanning({ specialPlanningId: specialPlannings.Id }));
			},
			reject: () => {
			}
		});
	}


	getValueMemory(): number {
		return Math.ceil((this.specialPlanningsMemorySize / this.specialPlanningsMaxMemorySize) * 100);
	}

	togglePanel(opened: boolean): void {
		this.opened = opened;
		this.dispatchGetSpecialPlanningsPaging();
	}

	correctSpecialPlannings(): void {
		if (this.specialPlannings != undefined && this.specialPlannings.length > 0) {
			for (let i = 0; i < this.specialPlannings.length; i++) {
				this.specialPlannings[i].Access = this.isAccessSpecialPlanning(this.specialPlannings[i]);
				this.specialPlannings[i].Old = this.isOldSpecialPlanning(this.specialPlannings[i]);
			}
		}
	}

	isAccessSpecialPlanning(specialPlanning: MtmaTimeslice): boolean {
		if ((specialPlanning.Flags & (1 << 0)) > 0) {
			return true;
		}
		return false;
	}


	isOldSpecialPlanning(specialPlanning: MtmaTimeslice): boolean {
		if ((specialPlanning.Flags & (1 << 1)) > 0) { // bit1
			this.thereIsOldTimeslice = true;
			return true;
		}
		if ((specialPlanning.Flags & (1 << 2)) > 0) { // bit2
			this.thereIsOldTimeslice = true;
			return true;
		}
		if ((specialPlanning.Flags & (1 << 3)) > 0) { // bit3
			this.thereIsOldTimeslice = true;
			return true;
		}
		if ((specialPlanning.Flags & (1 << 4)) > 0) { // bit4
			this.thereIsOldTimeslice = true;
			return true;
		}
		if ((specialPlanning.Flags & (1 << 5)) > 0) { // bit5
			this.thereIsOldTimeslice = true;
			return true;
		}
		if ((specialPlanning.Flags & (1 << 6)) > 0) { // bit6
			this.thereIsOldTimeslice = true;
			return true;
		}
		return false;
	}



	//---------------------OLD VERSIONE

	getRepetitionTimeslice(timeslice: MtmaTimeslice): number {
		const listFlag = [];
		let value = timeslice.Flags;
		for (let i = 3; i > -1; i--) {
			if (value !== 0 && value / (2 ** i) >= 1) {
				listFlag.push(1);
			} else {
				listFlag.push(0);
			}
			value = value % (2 ** i);
		}
		if (listFlag[3 - FlagTimeslice.REPETITION_MONTHLY] === 1) {
			return FlagTimeslice.REPETITION_MONTHLY;
		} else if (listFlag[3 - FlagTimeslice.REPETITION_ANNUAL] === 1) {
			return FlagTimeslice.REPETITION_ANNUAL;
		}
		return 0;
	}

	getLabelRepetition(repetition: number): string {
		switch (repetition) {
			case 2: {   // MONTLY
				return this.translate.instant('MONTHLY');
			}
			case 3: {   // ANNUAL
				return this.translate.instant('REPETITION_ANNUAL');
			}
			default: {
				return "-";
			}
		}
	}

	getColorRepetition(repetition: number): string {
		switch (repetition) {
			case 2: {   // MONTLY
				return 'w-10rem p-tag-lightBlue0220';
			}
			case 3: {   // ANNUAL
				return 'w-10rem p-tag-lightBlue0240';
			}
			default: {
				return 'w-10rem p-tag-darkBlue10';
			}
		}
	}


	getDetailsTimeSpecialPlanning(timeslice: MtmaTimeslice): string {
		let date1 = '';
		let date2 = '';
		let time1 = '';
		let time2 = '';
		const momFrom = moment.utc(timeslice.DtFrom);
		const momTo = moment.utc(timeslice.DtTo);
		const repetition = this.getRepetitionTimeslice(timeslice);
		if (repetition === FlagTimeslice.REPETITION_MONTHLY) {
			const listFlag = [];
			let value = timeslice.Flags;
			for (let i = 6; i > -1; i--) {
				if (value !== 0 && value / (2 ** i) >= 1) {
					listFlag.push(1);
				} else {
					listFlag.push(0);
				}
				value = value % (2 ** i);
			}
			let dayMonthStart = momFrom.utc().date();
			let dayMonthEnd = momTo.utc().date();
			if (listFlag[6 - FlagTimeslice.DATE_29] + listFlag[6 - FlagTimeslice.DATE_30] + listFlag[6 - FlagTimeslice.DATE_31] > 0) {
				if (listFlag[6 - FlagTimeslice.DATE_31] === 1) {
					dayMonthEnd = 31;
				}
				if (listFlag[6 - FlagTimeslice.DATE_30] === 1 && listFlag[6 - FlagTimeslice.DATE_31] === 1) {
					dayMonthStart = 30;
				} else if (listFlag[6 - FlagTimeslice.DATE_30] === 1 && listFlag[6 - FlagTimeslice.DATE_31] === 0) {
					dayMonthEnd = 30;
				}
				if (listFlag[6 - FlagTimeslice.DATE_29] === 1 && (listFlag[6 - FlagTimeslice.DATE_31] === 1 || listFlag[6 - FlagTimeslice.DATE_30] === 1)) {
					dayMonthStart = 29;
				} else if (listFlag[6 - FlagTimeslice.DATE_29] === 1 && listFlag[6 - FlagTimeslice.DATE_31] === 0 && listFlag[6 - FlagTimeslice.DATE_30] === 0) {
					dayMonthEnd = 29;
				}
			}
			date1 = this.translate.instant('DAY_FROM') + ' ' + dayMonthStart;
			date2 = this.translate.instant('DAY_TO') + ' ' + dayMonthEnd;
			/*detail = detail + ' ' + this.translate.instant('DAY_FROM') + ' ' + dayMonthStart +
				' - ' + this.translate.instant('DAY_TO') + ' ' + dayMonthEnd;*/
			time1 = this.translate.instant('HOURS') + ' ' + momFrom.utc().format('HH:mm');
			time2 = this.translate.instant('HOURS') + ' ' + momTo.utc().format('HH:mm');
			/*detail = detail + ' ' + this.translate.instant('DATE_FROM') + ' ' + momFrom.format('HH:mm') +
			' - ' + this.translate.instant('DATE_TO') + ' ' + momTo.format('HH:mm');*/
		} else if (repetition === FlagTimeslice.REPETITION_ANNUAL) {
			date1 = this.translate.instant('DAY_MONTH_FROM') + ' ' + momFrom.utc().format('DD/MM');
			date2 = this.translate.instant('DAY_MONTH_TO') + ' ' + momTo.utc().format('DD/MM');
			/*detail = detail + ' ' + this.translate.instant('DAY_MONTH_FROM') + ' ' + momFrom.utc().format('DD/MM') +
				' - ' + this.translate.instant('DAY_MONTH_TO') + ' ' + momTo.utc().format('DD/MM');*/
			time1 = this.translate.instant('HOURS') + ' ' + momFrom.utc().format('HH:mm');
			time2 = this.translate.instant('HOURS') + ' ' + momTo.utc().format('HH:mm');
			/*detail = detail + ' ' + this.translate.instant('DATE_FROM') + ' ' + momFrom.format('HH:mm') +
			' - ' + this.translate.instant('DATE_TO') + ' ' + momTo.format('HH:mm');*/
		}
		return date1 + ' ' + time1 + ' - ' + date2 + ' ' + time2;
	}


	getDetailsTimeslice(timeslice: MtmaTimeslice): string {
		let detail = '';
		const momFrom = moment(timeslice.DtFrom);
		const momTo = moment(timeslice.DtTo);
		if (timeslice.Type === 1) {
			const currentWeekDays = [];
			let value = timeslice.Weekdays;
			for (let i = 6; i > -1; i--) {
				if (value !== 0 && value / (2 ** i) >= 1) {
					currentWeekDays.unshift(i);
					detail = this.translate.instant(this.daysOfWeek.get(i)) + ' ' + detail;
				}
				value = value % (2 ** i);
			}
		} else if (timeslice.Type === 2) {
			const repetition = this.getRepetitionTimeslice(timeslice);
			if (repetition === FlagTimeslice.REPETITION_MONTHLY) {
				const listFlag = [];
				let value = timeslice.Flags;
				for (let i = 6; i > -1; i--) {
					if (value !== 0 && value / (2 ** i) >= 1) {
						listFlag.push(1);
					} else {
						listFlag.push(0);
					}
					value = value % (2 ** i);
				}
				let dayMonthStart = momFrom.utc().date();
				let dayMonthEnd = momTo.utc().date();
				if (listFlag[6 - FlagTimeslice.DATE_29] + listFlag[6 - FlagTimeslice.DATE_30] + listFlag[6 - FlagTimeslice.DATE_31] > 0) {
					if (listFlag[6 - FlagTimeslice.DATE_31] === 1) {
						dayMonthEnd = 31;
					}
					if (listFlag[6 - FlagTimeslice.DATE_30] === 1 && listFlag[6 - FlagTimeslice.DATE_31] === 1) {
						dayMonthStart = 30;
					} else if (listFlag[6 - FlagTimeslice.DATE_30] === 1 && listFlag[6 - FlagTimeslice.DATE_31] === 0) {
						dayMonthEnd = 30;
					}
					if (listFlag[6 - FlagTimeslice.DATE_29] === 1 && (listFlag[6 - FlagTimeslice.DATE_31] === 1 || listFlag[6 - FlagTimeslice.DATE_30] === 1)) {
						dayMonthStart = 29;
					} else if (listFlag[6 - FlagTimeslice.DATE_29] === 1 && listFlag[6 - FlagTimeslice.DATE_31] === 0 && listFlag[6 - FlagTimeslice.DATE_30] === 0) {
						dayMonthEnd = 29;
					}
				}
				detail = detail + ' ' + this.translate.instant('DAY_FROM') + ' ' + dayMonthStart +
					' - ' + this.translate.instant('DAY_TO') + ' ' + dayMonthEnd;
			} else if (repetition === FlagTimeslice.REPETITION_ANNUAL) {
				detail = detail + ' ' + this.translate.instant('DAY_MONTH_FROM') + ' ' + momFrom.utc().format('DD/MM') +
					' - ' + this.translate.instant('DAY_MONTH_TO') + ' ' + momTo.utc().format('DD/MM');
			}

		}
		return detail;
	}


	getTimeTimeslice(timeslice: MtmaTimeslice): string {
		const momFrom = moment(timeslice.DtFrom);
		const momTo = moment(timeslice.DtTo);


		let detail = '';
		detail = detail + ' ' + this.translate.instant('DATE_FROM') + ' ' + momFrom.format('HH:mm') +
			' - ' + this.translate.instant('DATE_TO') + ' ' + momTo.format('HH:mm');

		return detail;
	}

	getTemporaryTimeslice(timeslice: MtmaTimeslice): string {
		const listFlag = [];
		const momFrom = moment(timeslice.DtFrom);
		const momTo = moment(timeslice.DtTo);
		let value = timeslice.Flags;
		for (let i = 6; i > -1; i--) {
			if (value !== 0 && value / (2 ** i) >= 1) {
				listFlag.push(1);
			} else {
				listFlag.push(0);
			}
			value = value % (2 ** i);
		}
		let detail = '-';
		if (listFlag[6 - FlagTimeslice.TEMPORARY] === 1) {
			if (timeslice.Type === 1) {
				detail = this.translate.instant('FROM') + ' ' + momFrom.format('DD/MM/yyyy') +
					' - ' + this.translate.instant('TO') + ' ' + momTo.format('DD/MM/yyyy');
			} else if (timeslice.Type === 2) {
				const repetition = this.getRepetitionTimeslice(timeslice);
				if (repetition === FlagTimeslice.REPETITION_MONTHLY) {
					detail = this.translate.instant('DAY_MONTH_FROM') + ' ' + momFrom.format('MM/yyyy') +
						' - ' + this.translate.instant('DAY_MONTH_TO') + ' ' + momTo.format('MM/yyyy');
				} else if (repetition === FlagTimeslice.REPETITION_ANNUAL) {
					detail = this.translate.instant('FROM') + ' ' + momFrom.format('yyyy') +
						' - ' + this.translate.instant('TO') + ' ' + momTo.format('yyyy');
				}

			}
		}
		return detail;
	}

	//---------------------OLD VERSIONE


	// Design
	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	underTablet(): boolean {
		return StandardPageLayout.underTablet(this.screenWidth);
	}

	actionLayout(position: string): string {
		return StandardMtmaLayout.actionLayout(this.screenWidth, position);
	}

	layoutMarginResponsive(): string {
		return StandardPageLayout.layoutMarginResponsive(this.screenWidth);
	}

	dynamicInputSearchWidth(): string {
		return StandardMtmaLayout.dynamicInputSearchWidth(this.screenWidth, 'w-25rem');
	}
}
